/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { useAddEditAlarmForwarding } from "./hooks";
import AddEditAlarmModal from "./AddEditAlarmForwarding";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { setIsCreateSnmpGroup, setSelectedSnmpGroup } from "../../../../store/api/admin/reducers";
import { resetSnmpGroup } from "../../../helpers";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../../globals/constants";
import { prepareSnmpGroupCellDataGrid } from "../../../../pages/Configuration/helpers";
import { ISnmpDataGridRow } from "../../../../store/api/deviceConfig/types";
import { sanitizeHtmlInput } from "../../../../utils/helpers";
import { Button } from "../../../ui/button";
import { Icon } from "../../../Shared/Icon";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { GridColDef } from "@mui/x-data-grid";

const AlarmForwarding: React.FC = () => {
  const dispatch = useStoreDispatch();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");
  const [groupName, setGroupName] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Sync local drawer state with Redux (e.g., when drawer closes itself)
  useEffect(() => {
    if (basicDialogOpenFor !== DIALOGOPENFOR.ALARMFORWARDING) {
      setIsDrawerOpen(false);
    }
  }, [basicDialogOpenFor]);

  const { data, isLoading, alarmForwardingColumns, handleDeleteSnmpGroup } = useAddEditAlarmForwarding();

  const rowsData = prepareSnmpGroupCellDataGrid(data) || [];
  const noOfRows = rowsData?.length || 0;

  const handleOnAddSnmpGroup = (type: string) => {
    if (type === "Add") {
      dispatch(setSelectedSnmpGroup(resetSnmpGroup));
      dispatch(setIsCreateSnmpGroup(true));
      setModelOpenMode(type);
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ALARMFORWARDING));
    } else {
      if (groupName !== null) {
        dispatch(setIsCreateSnmpGroup(false));
        //update state of selectedSnmp Group from store after getting data from selectedGroup
        // dispatch(setSelectedSnmpGroup(resetSnmpGroup));
        setModelOpenMode(type);
        dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ALARMFORWARDING));
      }
      return;
      // getData fromApi
      //dispatch(setSelectedSnmpGroup(resetSnmpGroup));
    }
  };

  const handleEditRow = (row: any) => {
    dispatch(setSelectedSnmpGroup(row));
    dispatch(setIsCreateSnmpGroup(false));
    setModelOpenMode("Edit");
    setIsDrawerOpen(true);
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ALARMFORWARDING));
  };

  const handleDeleteRow = (row: any) => {
    setGroupName(row?.name);
    dispatch(setConfirmDialog(DIALOGOPENFOR.ALARMFORWARDING));
  };

  const handleDeleteSnmp = (actionName: string) => {
    if (!actionName) return;
    if (groupName !== null) {
      handleDeleteSnmpGroup(sanitizeHtmlInput(groupName), actionName);
    }
  };

  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground"
            aria-label="Row actions"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              const row = params.row;
              setTimeout(() => handleEditRow(row), 200);
            }}
          >
            <Pencil className="h-4 w-4" fill="currentColor" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => {
              const row = params.row;
              setTimeout(() => handleDeleteRow(row), 200);
            }}
          >
            <Trash2 className="h-4 w-4" fill="currentColor" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };

  return (
    <div className="flex flex-col justify-center items-start w-full">
      {openConfirmDialog === DIALOGOPENFOR.ALARMFORWARDING && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteSnmp}>
          Are you sure you want to delete <strong>{sanitizeHtmlInput(groupName) || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {(basicDialogOpenFor === DIALOGOPENFOR.ALARMFORWARDING || isDrawerOpen) && <AddEditAlarmModal modelOpenMode={modelOpenMode} />}
      <div className="flex items-center justify-end w-full px-3 mb-1">
        <Button variant="outline" size="md" onClick={() => handleOnAddSnmpGroup("Add")} style={{ backgroundColor: '#004f69', color: '#fff', borderColor: '#004f69' }}>
          <Icon name="add" size={18} />
          New SNMP Managers Group
        </Button>
      </div>
      
      <div className="flex justify-between items-end ml-3 mb-1 mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{`${noOfRows || 0} results`}</span>
        </div>
      </div>

      <div className="w-full pl-3 pr-3">
        <CustomDataGrid
          tableId="alarmForwarding"
          rows={rowsData}
          columns={[...alarmForwardingColumns, actionsColumn]}
          checkboxSelection={false}
          rowCount={noOfRows}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: ISnmpDataGridRow) => row.id}
          paginationMode="client"
          fillContainer
        />
      </div>
    </div>
  );
};

export default AlarmForwarding;
